import { useState, useCallback, useEffect } from 'react';
import { batchApi } from '../api/batchApi';
import { 
  BatchJob, 
  BatchStatistics, 
  BatchExecutionHistory,
  BatchJobType 
} from '../types/batch';

export const useBatchManagement = () => {
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([]);
  const [statistics, setStatistics] = useState<BatchStatistics | null>(null);
  const [runningJobs, setRunningJobs] = useState<BatchExecutionHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 배치 작업 목록 조회
  const refreshBatchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[배치 관리] 배치 작업 목록 조회 시작...');
      const response = await batchApi.getAllBatchJobs();
      console.log('[배치 관리] 응답:', response.data);
      
      if (response.data.success) {
        setBatchJobs(response.data.data);
        console.log('[배치 관리] 배치 작업 목록 조회 성공:', response.data.data.length, '개');
      } else {
        const errorMsg = response.data.message || '알 수 없는 에러';
        setError(errorMsg);
        console.error('[배치 관리] API 응답 실패:', errorMsg);
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || '배치 작업 목록 조회 실패';
      setError(message);
      console.error('[배치 관리] API 호출 실패:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: message,
        url: err.config?.url,
        error: err
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 통계 조회
  const refreshStatistics = useCallback(async () => {
    try {
      const response = await batchApi.getBatchStatistics();
      
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (err: any) {
      console.error('통계 조회 실패:', err);
    }
  }, []);

  // 실행 중인 배치 조회
  const refreshRunningJobs = useCallback(async () => {
    try {
      const response = await batchApi.getRunningBatchJobs();
      
      if (response.data.success) {
        setRunningJobs(response.data.data);
      }
    } catch (err: any) {
      console.error('실행 중인 배치 조회 실패:', err);
    }
  }, []);

  // 배치 수동 실행
  const executeManually = useCallback(async (jobType: BatchJobType): Promise<boolean> => {
    try {
      const response = await batchApi.executeManually(jobType);
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        // 실행 후 목록과 실행 중인 배치 갱신
        await refreshBatchJobs();
        await refreshRunningJobs();
        return true;
      } else {
        alert(`❌ ${response.data.message}`);
        return false;
      }
    } catch (err: any) {
      const message = err.response?.data?.message || '배치 실행 실패';
      
      // 중복 실행 에러는 더 명확하게 표시
      if (message.includes('이미 실행 중')) {
        alert(`⚠️ 중복 실행 방지\n\n${message}\n\n실행 중인 배치가 완료될 때까지 기다려주세요.`);
      } else {
        alert(`❌ ${message}`);
      }
      
      console.error('배치 실행 실패:', err);
      return false;
    }
  }, [refreshBatchJobs, refreshRunningJobs]);

  // 전체 데이터 새로고침
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshBatchJobs(),
      refreshStatistics(),
      refreshRunningJobs()
    ]);
  }, [refreshBatchJobs, refreshStatistics, refreshRunningJobs]);

  // 초기 로드 및 30초 자동 새로고침
  useEffect(() => {
    refreshAll();
    
    const interval = setInterval(() => {
      refreshAll();
    }, 30000); // 30초마다 자동 새로고침
    
    return () => clearInterval(interval);
  }, [refreshAll]);

  return {
    batchJobs,
    statistics,
    runningJobs,
    isLoading,
    error,
    refreshBatchJobs,
    refreshStatistics,
    refreshRunningJobs,
    refreshAll,
    executeManually
  };
};
